import { Button } from "@/app/common/common-components/ui/Button";
import { cn } from "@/app/common/common-functions/functions";
import { ChevronDown, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/common/common-components/ui/Popover";
import { useEffect, useState } from "react";

type Tag = {
  name: string;
  color: string;
  groupId: string;
};

type MultiTagSelectorProps = {
  tagGroups: {
    id: string;
    name: string;
    tags: Tag[];
  }[];
  onSelectedTagsChange?: (tags: Tag[]) => void;
  className?: string;
};

const MultiTagSelector = ({
  tagGroups,
  onSelectedTagsChange,
  className,
}: MultiTagSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const handleTagSelect = (tag: Tag) => {
    if (selectedTags.some((t) => t.name === tag.name)) {
      handleTagDeselect(tag);
    } else {
      setSelectedTags((prev) => [...prev, tag]);
    }
  };

  const handleTagDeselect = (tag: Tag) => {
    setSelectedTags((prev) => prev.filter((t) => t.name !== tag.name));
  };

  const handleTagDeselectAll = () => {
    setSelectedTags([]);
  };

  useEffect(() => {
    onSelectedTagsChange?.(selectedTags);
  }, [selectedTags]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn(className, "tw-font-normal")}>
          {selectedTags.length > 0
            ? `${selectedTags.length} Tag${selectedTags.length > 1 ? "s" : ""} Selected`
            : "Filter By Tags"}{" "}
          <ChevronDown className="tw-size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        onPointerDownOutside={(e) => e.preventDefault()}
        className="tw-flex tw-max-h-[300px] tw-w-full tw-max-w-[100dvw] tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-x-auto sm:tw-max-h-96 sm:tw-max-w-3xl"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <Search className="tw-size-4 tw-text-muted-foreground" />
          <input
            className="tw-w-full tw-outline-none"
            placeholder="Search tags"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {selectedTags.length > 0 && (
          <div className="tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-flex-wrap tw-gap-2">
            {selectedTags.map((tag) => (
              <TagItem
                key={tag.name}
                tag={tag}
                onDeselect={handleTagDeselect}
              />
            ))}
          </div>
        )}

        <div className="tw-flex tw-flex-nowrap tw-gap-2 tw-overflow-x-auto">
          {tagGroups.map((group) => (
            <div className="tw-flex tw-min-w-52 tw-flex-1 tw-flex-col tw-gap-2">
              <div className="tw-font-medium tw-text-muted-foreground">
                {group.name}
              </div>
              <div className="tw-flex tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-border">
                {group.tags
                  .filter(
                    (tag) => !selectedTags.some((t) => t.name === tag.name)
                  )
                  .filter((tag) =>
                    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((tag) => (
                    <TagListItem
                      key={tag.name}
                      tag={tag}
                      onSelect={handleTagSelect}
                    />
                  ))}
                {group.tags
                  .filter(
                    (tag) => !selectedTags.some((t) => t.name === tag.name)
                  )
                  .filter((tag) =>
                    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
                  ).length <= 0 && (
                  <div className="tw-py-2 tw-text-center tw-text-muted-foreground">
                    No more tags to select
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="tw-flex tw-gap-2 tw-self-end">
          <Button variant="outline" onClick={handleTagDeselectAll}>
            Deselect all
          </Button>
          <Button variant="default" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TagItem = ({
  tag,
  onDeselect,
}: {
  tag: Tag;
  onDeselect: (tag: Tag) => void;
}) => {
  return (
    <div
      className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-nowrap tw-rounded-full tw-border tw-border-input !tw-py-1 tw-px-2"
      onClick={() => onDeselect(tag)}
    >
      <div
        className="tw-size-2 tw-rounded-full"
        style={{ backgroundColor: tag.color }}
      />
      {tag.name} <X className="tw-size-3" />
    </div>
  );
};

const TagListItem = ({
  tag,
  onSelect,
}: {
  tag: Tag;
  onSelect: (tag: Tag) => void;
}) => {
  return (
    <div
      className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-p-2 hover:tw-bg-accent"
      onClick={() => onSelect(tag)}
    >
      <div
        className="tw-size-2 tw-rounded-full"
        style={{ backgroundColor: tag.color }}
      />
      <div className="">{tag.name}</div>
    </div>
  );
};

export default MultiTagSelector;
